<app-detail-panel [isOpen]="isOpen()" [resizable]="true" [(widthPx)]="width">
  @if (assessment(); as a) {
    <!-- Header -->
    <div class="panel-header">
      <div class="panel-title-row">
        <app-type-icon
          [icon]="getAssessmentIconConfig(a.assessment_type).icon"
          [variant]="getAssessmentIconConfig(a.assessment_type).variant"
        />
        <app-editable-panel-title
          [value]="a.assessment_name || ''"
          [placeholder]="'panel_assessment_info.untitled_placeholder' | transloco"
          [readonly]="!isSuperManager()"
          [ariaLabel]="'panel_assessment_info.name_aria' | transloco"
          (commit)="onTitleCommit($event, a)"
        />
      </div>
      <div class="panel-header-actions">
        @if (!isRecordOnly()) {
          <button
            class="panel-icon-btn"
            (click)="onOpenInNewTab()"
            [attr.aria-label]="'panel_assessment_info.open_in_new_tab' | transloco"
            [matTooltip]="'panel_assessment_info.see_full_details' | transloco"
            matTooltipPosition="below"
          >
            <mat-icon>open_in_new</mat-icon>
          </button>
        }
        <button
          class="panel-icon-btn"
          [attr.aria-label]="'panel_assessment_info.more_options_aria' | transloco"
          [matMenuTriggerFor]="menu"
        >
          <mat-icon>more_vert</mat-icon>
        </button>
        <button
          class="panel-icon-btn"
          (click)="onClose()"
          [attr.aria-label]="'panel_assessment_info.close_panel_aria' | transloco"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Sub-hero — STATE ONLY. Two state axes for an assessment:
         lifecycle (Completed / In progress / Failed) leads; review
         state (Reviewed / Not submitted / In review / Failed) follows.
         Type taxonomy dropped — the corner `<app-type-icon>` already
         carries the DPIA / AI Risk identity. Updated date dropped —
         descriptive metadata belongs in the body's Details grid, not
         at-a-glance identity chrome. Follows the same principle
         applied to the control drawer. -->
    <div class="panel-sub-hero">
      <div class="panel-meta-row">
        <app-status-indicator
          appearance="inline"
          [status]="getAssessmentStatusVariant(a.assessment_status)"
          [meaning]="getAssessmentStatusMeaning(a.assessment_status)"
          [label]="getAssessmentStatusDisplayName(a.assessment_status)"
        />
        @if (a.review_status === 'REVIEWED') {
          <app-status-indicator
            appearance="inline"
            [status]="getReviewStatusVariant(a.review_status)"
            [meaning]="getReviewStatusMeaning(a.review_status)"
            [label]="getReviewStatusLabel(a.review_status)"
          />
        } @else if (a.review_status; as rs) {
          <app-status-indicator
            appearance="inline"
            [status]="getReviewStatusVariant(rs)"
            [meaning]="getReviewStatusMeaning(rs)"
            [label]="getReviewStatusLabel(rs)"
          />
        }
      </div>

      <!-- DPIA sub-hero row — always-visible identity context that AI Risk
           doesn't have: the linked processing activity + the one-liner
           screening outcome. Both interactive; PA opens the activity page,
           the screening chip jumps to the Report tab where the criteria
           are expanded. Hidden entirely when neither piece of context
           exists so the sub-hero doesn't collapse to an empty second row. -->
      @if (hasDpiaSubHero()) {
        <div class="panel-meta-row panel-meta-row--dpia">
          @if (a.pa_link) {
            <!-- Anchor with `target="_blank"` so left-click opens the PA
                 edit modal in a new tab (keeping the DPIA drawer intact
                 for cross-referencing), and cmd/middle/right-click give
                 the same browser-native new-tab behaviour. No SPA click
                 interceptor — the browser does the right thing. -->
            <a
              class="dpia-hero-chip"
              [attr.href]="linkedPaHref()"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="'panel_assessment_info.dpia.open_linked_pa_aria' | transloco"
              [matTooltip]="'panel_assessment_info.dpia.open_linked_pa_tooltip' | transloco"
              matTooltipPosition="below"
            >
              <mat-icon aria-hidden="true">open_in_new</mat-icon>
              <span class="dpia-hero-chip__label">
                {{
                  linkedActivityName() ||
                    ('panel_assessment_info.dpia.linked_pa_generic' | transloco)
                }}
              </span>
            </a>
          }
          @if (screeningSummary(); as s) {
            @if (a.pa_link) {
              <span class="panel-meta-dot" aria-hidden="true">·</span>
            }
            <button
              type="button"
              class="dpia-hero-chip dpia-hero-chip--ghost"
              (click)="onJumpToReport()"
              [attr.aria-label]="'panel_assessment_info.dpia.open_screening_aria' | transloco"
            >
              <span class="dpia-hero-chip__caption">
                {{ 'panel_assessment_info.dpia.screening_prefix' | transloco }}
              </span>
              <span class="dpia-hero-chip__label">{{ s.labelKey | transloco }}</span>
              @if (s.criteriaCount > 0) {
                <span class="dpia-hero-chip__count">({{ s.criteriaCount }})</span>
              }
            </button>
          }
        </div>
      }
    </div>

    <!-- More menu -->
    <mat-menu #menu="matMenu" class="panel-more-menu">
      @if (isRecordOnly()) {
        <!-- VER-377/383 — recorded derogation: Share, "Create TIA" while
             open (in place of Delete), Delete once superseded. -->
        <button mat-menu-item (click)="onShare()" [disabled]="!canExport()">
          <mat-icon>share</mat-icon>
          <span>{{ 'panel_assessment_info.menu_share' | transloco }}</span>
        </button>
        @if (canStartFullTia()) {
          <button mat-menu-item (click)="onStartFullTia()">
            <mat-icon>assignment</mat-icon>
            <span>{{ 'assessment.menu_create_tia' | transloco }}</span>
          </button>
        }
        @if (isRetiredRecordOnly()) {
          <button
            mat-menu-item
            class="menu-item-danger"
            (click)="onDelete()"
            [disabled]="!canDeleteAssessment(a)"
            [title]="deleteDisabledTooltip(a)"
          >
            <mat-icon>delete</mat-icon>
            <span>{{ 'common.delete' | transloco }}</span>
          </button>
        }
      } @else {
        <button
          mat-menu-item
          (click)="onExport()"
          [disabled]="isExporting() || !canExport()"
          [matTooltip]="exportDisabledTooltip()"
          matTooltipPosition="left"
        >
          @if (isExporting()) {
            <mat-icon class="ai-spinner-icon">hourglass_empty</mat-icon>
          } @else {
            <mat-icon>download</mat-icon>
          }
          <span>{{ 'panel_assessment_info.menu_export' | transloco }}</span>
        </button>

        <button
          mat-menu-item
          (click)="onShare()"
          [disabled]="!canExport()"
          [matTooltip]="exportDisabledTooltip()"
          matTooltipPosition="left"
        >
          <mat-icon>share</mat-icon>
          <span>{{ 'panel_assessment_info.menu_share' | transloco }}</span>
        </button>

        @if (
          canRequestReview() &&
          a.assessment_status === 'COMPLETED' &&
          (a.review_status === 'PENDING' || !a.review_status)
        ) {
          <button mat-menu-item (click)="onRequestReview()">
            <mat-icon>rate_review</mat-icon>
            <span>{{ 'panel_assessment_info.menu_request_review' | transloco }}</span>
          </button>
        }

        @if (canApproveDirectly()) {
          <button mat-menu-item (click)="onApprove()">
            <app-action-glyph name="verified-seal" />
            <span>{{ 'panel_assessment_info.menu_approve' | transloco }}</span>
          </button>
        }

        <button
          mat-menu-item
          class="menu-item-danger"
          (click)="onDelete()"
          [disabled]="isAssessmentLocked(a) || !canDeleteAssessment(a)"
          [title]="deleteDisabledTooltip(a)"
        >
          <mat-icon>delete</mat-icon>
          <span>{{ 'common.delete' | transloco }}</span>
        </button>
      }
    </mat-menu>

    <!-- Tabs -->
    <app-panel-tabs
      [tabs]="panelTabs()"
      [activeTab]="activeTab()"
      (tabChange)="setTab($any($event))"
    />

    <!-- Scrollable body -->
    <div class="panel-body" [class.panel-body--flush]="activeTab() === 'risk'" role="tabpanel">
      <!-- ── OVERVIEW TAB ──
           Universal identity block: description, details grid, trust-centre
           toggle, labels, context documents. DPIA-specific content
           (screening, linked PA, risk snapshot, sign-off) lives on the
           Report tab; the sub-hero surfaces the linked-PA / screening
           one-liner so those signals stay visible without opening a tab. -->
      @if (activeTab() === 'overview') {
        @if (isRecordOnly()) {
          <div class="panel-section">
            <app-tia-record-summary [assessment]="a" [activityName]="linkedActivityName()" />
            @if (canStartFullTia()) {
              <!-- VER-383 — same flow as the wizard's "Continue with TIA",
                   prefilled from the record. -->
              <app-button
                class="ai-record-create-tia"
                type="primary"
                size="medium"
                borderRadius="6px"
                (clicked)="onStartFullTia()"
              >
                {{ 'assessment.menu_create_tia' | transloco }}
              </app-button>
            }
          </div>
        }
        <!-- Description -->
        <div class="panel-section">
          <div class="panel-section-label">
            {{ 'panel_assessment_info.section_description' | transloco }}
          </div>
          @if (a.assessment_description) {
            <p
              class="panel-desc-text"
              [class.panel-desc-text--clamped]="descriptionNeedsToggle() && !descriptionExpanded()"
            >
              {{ a.assessment_description }}
            </p>
            @if (descriptionNeedsToggle()) {
              <button
                type="button"
                class="panel-desc-toggle"
                [attr.aria-expanded]="descriptionExpanded()"
                (click)="descriptionExpanded.set(!descriptionExpanded())"
              >
                <span>{{
                  (descriptionExpanded()
                    ? 'panel_assessment_info.show_less'
                    : 'panel_assessment_info.show_more'
                  ) | transloco
                }}</span>
                <mat-icon aria-hidden="true">expand_more</mat-icon>
              </button>
            }
          } @else {
            <p class="ai-empty-text">{{ 'panel_assessment_info.no_description' | transloco }}</p>
          }
        </div>

        <!-- Details grid -->
        <div class="panel-section">
          <div class="panel-section-label">
            {{ 'panel_assessment_info.section_details' | transloco }}
          </div>
          <div class="panel-details-grid">
            @if (creatorDisplayName(); as name) {
              <div class="panel-detail-item">
                <div class="panel-detail-label">
                  {{ 'panel_assessment_info.field_created_by' | transloco }}
                </div>
                <div class="panel-detail-value">
                  <div class="panel-avatar-row">
                    <span class="panel-avatar" [style.background]="getAvatarColor(name)">
                      {{ getInitials(name) }}
                    </span>
                    {{ name }}
                  </div>
                </div>
              </div>
            }
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panel_assessment_info.field_author_org' | transloco }}
              </div>
              <div class="panel-detail-value">{{ a.created_by_org_name || '—' }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panel_assessment_info.field_owner' | transloco }}
              </div>
              <div class="panel-detail-value">
                {{ a.customer_org_name || getDisplayOrgName(a) || '—' }}
              </div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panel_assessment_info.field_status' | transloco }}
              </div>
              <div class="panel-detail-value">
                {{ getAssessmentStatusDisplayName(a.assessment_status) }}
              </div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panel_assessment_info.field_last_updated' | transloco }}
              </div>
              <div class="panel-detail-value">{{ a.updated_at | date: 'mediumDate' }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panel_assessment_info.field_edits' | transloco }}
              </div>
              <div class="panel-detail-value">{{ a.edits_count || 0 }}</div>
            </div>
            @if (a.language) {
              <div class="panel-detail-item">
                <div class="panel-detail-label">
                  {{ 'language_picker.label' | transloco }}
                </div>
                <div class="panel-detail-value">
                  <app-language-badge [language]="a.language" appearance="inline" />
                </div>
              </div>
            }
          </div>
        </div>

        <app-trust-center-visibility-toggle
          kind="assessments"
          [entityId]="a.assessment_id"
          [eligible]="isTrustCenterEligible()"
        />

        <!-- Labels -->
        <div class="panel-section">
          <div class="panel-section-label">
            {{ 'panel_assessment_info.section_labels' | transloco }}
          </div>
          <div class="ai-labels-chips">
            @if ((a.label_ids | labelNames).length > 0) {
              @for (name of a.label_ids | labelNames; track name) {
                <span class="ai-label-chip">{{ name }}</span>
              }
            } @else {
              <span class="ai-empty-text">{{
                'panel_assessment_info.labels_none' | transloco
              }}</span>
            }
          </div>
        </div>

        @if (a.assessment_status === 'COMPLETED' && (a.context_document_ids?.length ?? 0) > 0) {
          <div class="panel-section">
            <div class="panel-section-label">
              <span>{{ 'panel_assessment_info.section_context_documents' | transloco }}</span>
              <!-- Show the badge while loading (expected count from IDs) and
                   after load when at least one doc resolved. Hide it when the
                   resolved count is 0 so a stale-FK assessment doesn't read
                   "1" above an empty-state message. -->
              @if (displayedContextDocCount() > 0) {
                <span class="context-docs-count" aria-hidden="true">
                  {{ displayedContextDocCount() }}
                </span>
              }
            </div>
            <!-- Three mutually-exclusive states. The bordered `.context-docs-list`
                 container is only rendered when there is actual content (skeletons
                 or rows) — never as an empty stroked rectangle. -->
            @if (contextDocsError(); as message) {
              <app-access-banner
                [fullWidth]="true"
                [notice]="{ message, icon: 'error', tone: 'error' }"
              >
                <app-button
                  noticeActions
                  type="secondary"
                  variant="outline"
                  size="small"
                  (clicked)="retryContextDocs()"
                >
                  {{ 'common.retry' | transloco }}
                </app-button>
              </app-access-banner>
            }
            @if (contextDocsError() && !isLoadingContextDocs() && usedContextDocs().length === 0) {
              <app-empty-state
                variant="inline"
                icon="link_off"
                [title]="'panel_assessment_info.context_docs_unavailable_title' | transloco"
                [message]="'panel_assessment_info.context_docs_unavailable' | transloco"
              />
            } @else if (contextDocsEmptyAfterLoad()) {
              <app-empty-state
                variant="inline"
                icon="link_off"
                [title]="'panel_assessment_info.context_docs_unavailable_title' | transloco"
                [message]="'panel_assessment_info.context_docs_unavailable' | transloco"
              />
            } @else {
              <ul class="context-docs-list" role="list">
                @if (isLoadingContextDocs()) {
                  @for (id of a.context_document_ids ?? []; track id) {
                    <li class="context-doc-item" aria-hidden="true">
                      <div appSkeleton="circle" class="context-doc-skel-icon"></div>
                      <div class="context-doc-info">
                        <div
                          appSkeleton
                          class="context-doc-skel-line context-doc-skel-line--name"
                        ></div>
                        <div
                          appSkeleton
                          class="context-doc-skel-line context-doc-skel-line--meta"
                        ></div>
                      </div>
                    </li>
                  }
                } @else {
                  @for (ctxDoc of usedContextDocs(); track ctxDoc.context_document_id) {
                    <li class="context-doc-item">
                      <app-type-icon
                        [icon]="getContextDocIconConfig(ctxDoc.file_type).icon"
                        [variant]="getContextDocIconConfig(ctxDoc.file_type).variant"
                        size="small"
                      />
                      <div class="context-doc-info">
                        <span class="context-doc-name" [title]="ctxDoc.file_name">
                          {{ ctxDoc.file_name }}
                        </span>
                        <span class="context-doc-desc">
                          {{ getContextDocFileTypeLabel(ctxDoc.file_type) }}
                          @if (ctxDoc.file_size) {
                            &middot; {{ formatFileSize(ctxDoc.file_size) }}
                          }
                        </span>
                      </div>
                      <button
                        type="button"
                        class="panel-icon-btn"
                        (click)="contextDocPreview.open(ctxDoc)"
                        [attr.aria-label]="'Preview ' + ctxDoc.file_name"
                        title="Preview"
                      >
                        <mat-icon>visibility</mat-icon>
                      </button>
                    </li>
                  }
                }
              </ul>
            }
          </div>
        }
      }

      <!-- ── TASKS TAB ── -->
      @if (a.assessment_id) {
        <app-panel-task-list
          [style.display]="activeTab() !== 'tasks' ? 'none' : null"
          [orgId]="a.org_id"
          linkedField="assessment_id"
          [linkedId]="a.assessment_id"
          [isManager]="!!(isManager() || isSuperManager())"
          [showRecurring]="false"
          [showLinkPicker]="true"
          (taskCount)="onTaskCountChanged($event)"
          (requestClose)="panelClose.emit()"
        />
      }

      <!-- ── REPORT / MODEL CARD TAB ──
           Type-dispatched body. DPIA → the compact Report surface
           (risk snapshot, prior-consultation callout, risks list,
           sign-off ledger). AI Risk → the Model Card summary. Tab
           label switches label alongside via `panelTabs()`. -->
      @if (activeTab() === 'risk') {
        @if (isDpia()) {
          <app-dpia-drawer-report [dpia]="dpiaProfile()" />
        } @else {
          <app-model-card-summary
            variant="panel"
            [riskProfile]="a.risk_profile ?? null"
            [generatedContent]="a.generated_content ?? null"
            [assessmentId]="a.assessment_id"
            [assessmentName]="
              a.assessment_name || ('panel_assessment_info.untitled_placeholder' | transloco)
            "
            [assessmentDescription]="a.assessment_description"
            [preparedDate]="a.updated_at"
            [reviewStatus]="a.review_status ?? null"
            [canExport]="canExport()"
            [exportTooltip]="exportDisabledTooltip()"
            [isExporting]="isExporting()"
            (exportRequested)="onExport()"
          />
        }
      }

      <!-- ── HISTORY TAB ──
           Audit timeline for the assessment. Sourced from
           `GET /organizations/{orgId}/audit-trail?entity_type=assessment&entity_id=<id>`;
           the loader reloads whenever the panel switches to a different
           assessment (constructor effect). -->
      @if (activeTab() === 'history') {
        <app-audit-timeline
          [events]="auditLoader.auditTrail()"
          [loading]="auditLoader.loadingAuditTrail()"
          [loadFailed]="auditLoader.loadError() !== null"
          [entityLabel]="'panel_assessment_info.entity_label_assessment' | transloco"
          (refresh)="auditLoader.load()"
        />
      }
    </div>
  } @else {
    <div class="ai-loading-state">
      <p>Select an assessment to view details</p>
    </div>
  }
</app-detail-panel>

<!-- Shared preview modal — single mount for the Used-for-Generation
     list. The state machine (open/close/retry/openInNewTab) lives in
     `shared/context-doc-preview-handler.ts`; the modal itself is the
     same component the drawer / vendor evidence-tab use. -->
<app-context-doc-preview [handler]="contextDocPreview" />
